import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, Upload, Trash2, Download, Database, AlertTriangle } from "lucide-react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  if (!s) return null;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gogojalan-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Backup diunduh — ${data.total_documents} dokumen`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal export data");
    } finally {
      setBackupBusy(false);
    }
  };

  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm(`Restore data dari "${file.name}"? Data existing dengan ID yang sama akan ditimpa. Data lain di database ini tetap ada.`)) return;
    setRestoreBusy(true);
    setRestoreResult(null);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      if (!payload?.collections) throw new Error("File backup tidak valid");
      const { data } = await api.post("/admin/import", payload);
      setRestoreResult(data.summary);
      toast.success("Restore selesai");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Gagal restore");
    } finally {
      setRestoreBusy(false);
    }
  };

  const save = async () => {
    await api.put("/settings", s);
    window.dispatchEvent(new Event("brand-updated"));
    toast.success("Setelan tersimpan");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Ukuran maksimal 3 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((x) => ({ ...x, logo_data_url: data.logo_data_url }));
      window.dispatchEvent(new Event("brand-updated"));
      toast.success("Logo diunggah");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    if (!window.confirm("Hapus logo dan kembali ke logo default?")) return;
    await api.delete("/settings/logo");
    setS((x) => ({ ...x, logo_data_url: "" }));
    window.dispatchEvent(new Event("brand-updated"));
    toast.success("Logo dihapus");
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Settings</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Informasi perusahaan & logo untuk invoice, e-ticket, dan header.</p>
        </div>
        <Button onClick={save} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="settings-save"><Save size={14} className="mr-1"/> Save</Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Logo Perusahaan</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
              {s.logo_data_url ? (
                <img src={s.logo_data_url} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
              ) : (
                <span className="text-slate-300 text-xs">No logo</span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 mb-2">Logo tampil di sidebar, halaman login, invoice PDF, dan e-ticket PDF.</p>
              <p className="text-xs text-slate-400 mb-3">Format: PNG/JPG/SVG · Maks 3 MB · Disarankan persegi (≥ 512×512).</p>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="logo-upload-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#556b2f] hover:bg-[#3d4d1e]" data-testid="logo-upload-btn">
                  <Upload size={14} className="mr-1"/> {uploading ? "Mengunggah..." : "Upload Logo"}
                </Button>
                {s.logo_data_url && (
                  <Button variant="outline" onClick={clearLogo} data-testid="logo-clear-btn"><Trash2 size={14} className="mr-1"/> Hapus</Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Company Info</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label>Company Name</Label><Input value={s.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="set-company"/></div>
          <div className="sm:col-span-2"><Label>Tagline</Label><Input value={s.tagline} onChange={(e) => set("tagline", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>Alamat</Label><Input value={s.address} onChange={(e) => set("address", e.target.value)}/></div>
          <div><Label>WhatsApp</Label><Input value={s.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="set-wa"/></div>
          <div><Label>Email</Label><Input value={s.email} onChange={(e) => set("email", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>Rekening / Bank Account</Label><Input value={s.bank_account} onChange={(e) => set("bank_account", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>Footer Invoice</Label><Textarea value={s.footer_invoice} onChange={(e) => set("footer_invoice", e.target.value)}/></div>
          <div className="sm:col-span-2"><Label>Footer E-ticket</Label><Textarea value={s.footer_eticket} onChange={(e) => set("footer_eticket", e.target.value)}/></div>
        </CardContent>
      </Card>
      <Card data-testid="backup-restore-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Database size={18} className="text-[#556b2f]"/> Backup & Restore Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Migrasi data antar environment (preview ↔ production ↔ VPS). Backup mencakup Invoice, E-Ticket, Hotel, Booking, Customer, Expense, Settings, dan Counters. Kredensial admin tidak ikut.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="font-semibold text-slate-900 mb-1">1. Download Backup</div>
              <p className="text-xs text-slate-500 mb-3">Simpan seluruh data database saat ini sebagai file JSON.</p>
              <Button
                onClick={downloadBackup}
                disabled={backupBusy}
                className="w-full bg-[#556b2f] hover:bg-[#3d4d1e]"
                data-testid="backup-download-btn"
              >
                <Download size={14} className="mr-1"/> {backupBusy ? "Menyiapkan..." : "Download JSON"}
              </Button>
            </div>

            <div className="border border-slate-200 rounded-lg p-4">
              <div className="font-semibold text-slate-900 mb-1">2. Restore dari file</div>
              <p className="text-xs text-slate-500 mb-3">Import data dari file backup JSON. Aman: data dengan ID sama akan di-update, sisanya tetap.</p>
              <input
                ref={restoreRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={restoreBackup}
                data-testid="restore-file-input"
              />
              <Button
                onClick={() => restoreRef.current?.click()}
                disabled={restoreBusy}
                variant="outline"
                className="w-full"
                data-testid="restore-btn"
              >
                <Upload size={14} className="mr-1"/> {restoreBusy ? "Memproses..." : "Pilih File Backup"}
              </Button>
            </div>
          </div>

          {restoreResult && (
            <div className="border border-[#e4ecc9] bg-[#fafcf3] rounded-lg p-3" data-testid="restore-summary">
              <div className="text-sm font-semibold text-[#3d4d1e] mb-2">Hasil restore:</div>
              <div className="text-xs text-slate-700 space-y-0.5 mono">
                {Object.entries(restoreResult).map(([coll, s]) => (
                  <div key={coll}>
                    <b>{coll}</b>: +{s.inserted} baru · ↻{s.updated} update{s.skipped ? ` · ${s.skipped} skip` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            <span>Simpan file backup di tempat aman. File berisi seluruh data operasional (termasuk PDF asli OTA dalam base64) — bisa besar (10-100 MB).</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
